import { IoClose } from 'react-icons/io5';
import { ClinicalSpecialistUrl } from '@icons';
import { Tooltip, Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Spinner, Image } from '@ds';
import { useHubResources } from '@lp/features/RoutePlanner/hooks/useHubResources';
import { TmdxResourceCard } from '@features/components/ResourceCards';
import SectionRow from '@features/components/CaseOverview/components/SectionRow/SectionRow';
import { HubResourcesCount } from '@lp/features/RoutePlanner/types';
import {
  getColorsByOrganType,
  getFullName,
  getLocationAddress,
  getOrganImg,
  getShortTzByIanaName,
} from '@/utils/helpers';
import { OrganType } from '@/api/apis/case';
import { EmployeeRole } from '@/api/apis/resources';
import { useHubItemByIdQuery } from '@/queries/hub';
import { Hub } from '@/api/apis/facilities';
import { useCountriesData } from '@/contexts/CountriesDataProvider';

export type ViewHubModalProps = {
  title: string;
  hubId: string;
  isOpen: boolean;
  isLoading?: boolean;
  size?: 'md' | 'xs' | 'sm' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | 'full';
  onRemove: () => void;
  onClose: () => void;
};

type HubModalContentProps = {
  hub: Hub;
  resources: HubResourcesCount;
};

export const HubModalContent = ({ hub, resources }: HubModalContentProps) => {
  const { resolveSubdivisionName } = useCountriesData();
  const colorsHeart = getColorsByOrganType(OrganType.Heart);
  const colorsLiver = getColorsByOrganType(OrganType.Liver);
  const colorsLungs = getColorsByOrganType(OrganType.Lungs);
  const colorsKidney = getColorsByOrganType(OrganType.Kidney);

  return (
    <>
      <div className="flex flex-col gap-4 p-6 bg-base-90 rounded-xl shadow-s">{`${hub?.name} (${hub?.code})`}</div>
      <div className="flex flex-col gap-4 p-6 bg-base-70 rounded-xl shadow-s">
        <SectionRow label="Timezone">{getShortTzByIanaName(hub.location.timezone)}</SectionRow>
        <SectionRow label="Region">{hub?.location.region}</SectionRow>
        <SectionRow label="Address">
          <Tooltip placement="top" content={getLocationAddress(hub.location, resolveSubdivisionName)}>
            {getLocationAddress(hub.location, resolveSubdivisionName)}
          </Tooltip>
        </SectionRow>
        <SectionRow label="Resources">
          <div className="flex items-center gap-3 *:shrink-0">
            <div className="flex items-center gap-1">
              <div className={`${colorsHeart.bg50} w-6 h-6 rounded-full flex justify-center items-center`}>
                <Image className="w-5 h-5" src={getOrganImg(OrganType.Heart)} alt={`${OrganType.Heart} image`} />
              </div>
              <p className="text-l-r">{resources[OrganType.Heart]}</p>
            </div>
            <div className="flex items-center gap-1">
              <div className={`${colorsLiver.bg50} w-6 h-6 rounded-full flex justify-center items-center`}>
                <Image className="w-5 h-5" src={getOrganImg(OrganType.Liver)} alt={`${OrganType.Liver} image`} />
              </div>
              <p className="text-l-r">{resources[OrganType.Liver]}</p>
            </div>
            <div className="flex items-center gap-1">
              <div className={`${colorsLungs.bg50} w-6 h-6 rounded-full flex justify-center items-center`}>
                <Image className="w-5 h-5" src={getOrganImg(OrganType.Lungs)} alt={`${OrganType.Lungs} image`} />
              </div>
              <p className="text-l-r">{resources[OrganType.Lungs]}</p>
            </div>
            <div className="flex items-center gap-1">
              <div className={`${colorsKidney.bg50} w-6 h-6 rounded-full flex justify-center items-center`}>
                <Image className="w-5 h-5" src={getOrganImg(OrganType.Kidney)} alt={`${OrganType.Kidney} image`} />
              </div>
              <p className="text-l-r">{resources[OrganType.Kidney]}</p>
            </div>
            <div className="flex items-center gap-1">
              <div className="bg-base-40 w-6 h-6 rounded-full flex justify-center items-center">
                <Image className="w-4 h-4" src={ClinicalSpecialistUrl} alt="Clinical Specialist image" />
              </div>
              <p className="text-l-r">{resources[EmployeeRole.ClinicalSpecialist]}</p>
            </div>
          </div>
        </SectionRow>
        {!!hub.regionalManager && (
          <TmdxResourceCard
            list={[]}
            type="user"
            size="sm"
            title={getFullName(hub.regionalManager.firstName, hub.regionalManager.lastName)}
            subTitle="Regional Manager"
            showChip={false}
            avatarProps={{
              type: 'user',
              src: hub.regionalManager.profilePhoto,
            }}
          />
        )}
        {!!hub.nationalManager && (
          <TmdxResourceCard
            list={[]}
            type="user"
            size="sm"
            showChip={false}
            title={getFullName(hub.nationalManager.firstName, hub.nationalManager.lastName)}
            subTitle="National Manager"
            avatarProps={{
              type: 'user',
              src: hub.nationalManager.profilePhoto,
            }}
          />
        )}
      </div>
    </>
  );
};

export const ViewHubModal = ({
  title,
  hubId,
  isOpen,
  isLoading,
  size = 'xl',
  onRemove,
  onClose,
}: ViewHubModalProps) => {
  const { data: hub, isFetched: isHubFetched } = useHubItemByIdQuery(hubId);
  const { getCountedResources, isLoading: isResourcesLoading } = useHubResources({ enabled: true });

  const resources = getCountedResources(hub);

  const showLoader = isLoading || !isHubFetched || isResourcesLoading;

  return (
    <Modal
      size={size}
      isOpen={isOpen}
      onClose={onClose}
      backdrop="opaque"
      closeButton={<IoClose size={45} style={{ top: 15 }} />}
      classNames={{
        base: 'px-6 bg-base-80',
        body: 'border-y-1 border-base-30 px-0 py-6 text-l-r',
        header: 'px-0 font-secondary text-hl! text-base-20',
        footer: 'px-0 flex gap-6',
        wrapper: 'z-420',
        backdrop: 'z-419',
      }}
    >
      <ModalContent>
        {() => (
          <>
            {showLoader && (
              <div className="absolute top-0 left-0 flex justify-center items-center z-50 w-full h-full bg-white bg-opacity-40">
                <Spinner size="md" />
              </div>
            )}
            <ModalHeader>{title}</ModalHeader>
            <ModalBody>{hub && <HubModalContent hub={hub} resources={resources} />}</ModalBody>
            <ModalFooter>
              <Button isDisabled={isLoading} size="md" color="default" variant="outline" onClick={onRemove}>
                Remove
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
